let _socket = null;
// Subscribers registered via subscribeToWhiteboardSocket(). Notified any
// time getWhiteboardSocket() swaps in a brand-new WebSocket (fresh open,
// board/question change, or a dead-socket reconnect) or resetWhiteboardSocket()
// clears it. This is what lets React components react to the singleton
// changing instead of only picking it up whenever they happen to re-render
// for some unrelated reason — which was the source of the "socket goes
// stale after a network drop, whiteboard looks laggy / buttons stop doing
// anything until some other state change forces a re-render" bug.
const _subscribers = new Set();

function _notifySubscribers() {
  _subscribers.forEach((cb) => {
    try {
      cb(_socket);
    } catch (err) {
      console.warn("[Whiteboard] socket subscriber threw:", err);
    }
  });
}

// Register a callback to be invoked with the current socket every time the
// singleton changes. Returns an unsubscribe function. Call this from a
// useEffect and store the pushed value in React state — do NOT rely on
// calling getWhiteboardSocket() directly in a render body if you need to
// stay in sync with reconnects.
export function subscribeToWhiteboardSocket(cb) {
  _subscribers.add(cb);
  return () => _subscribers.delete(cb);
}
// Which boardId (sessionId) the current _socket was opened for. Lets
// getWhiteboardSocket() detect a stale board even if resetWhiteboardSocket()
// was somehow skipped, instead of silently reusing the wrong room.
let _socketBoardId = null;
// Which questionId the current _socket was opened for. A question change
// (e.g. advancing to the next whiteboard question, or a reconnect resolving
// to a different question than a fresh fetch would) should also force a
// brand new connection — same safety-net role as _socketBoardId above.
let _socketQuestionId = null;

const WB_BASE = process.env.REACT_APP_WEB_SOCKET_URL;

if (!WB_BASE) {
  // Without the REACT_APP_ prefix, CRA won't inject the var into the
  // client bundle — WB_BASE silently becomes "undefined" and the socket
  // URL resolves as a relative path against the current origin instead
  // of the real backend. Fail loudly here instead of connecting to the
  // wrong host.
  console.error(
    "[Whiteboard] REACT_APP_WEB_SOCKET_URL is not set. " +
      "Check your .env file uses the REACT_APP_ prefix and that the dev " +
      "server was restarted after editing .env.",
  );
}

// boardId defaults to "board-1" only for backwards-compatible call sites
// (e.g. useWhiteboardRecording's module-level WB_SERVER, which only needs a
// stable base URL since it appends sessionId to each request itself).
// WhiteboardView passes the real per-question sessionId so every question
// gets its own room on the wire, not just in the event payloads.
//
// questionId comes from get-whiteboard-coding-questions' `question_id` for a
// fresh open, or from the pre-reconnection-check payload's
// `whiteboard_question_id` when resuming an in-progress whiteboard. It's
// required — logged loudly (not thrown) if missing, matching this file's
// existing style, so a bad caller doesn't crash the whole meeting.
export function getWhiteboardPath(boardId = "board-1", questionId) {
  const meetingId = sessionStorage.getItem("meetingId");
  const interviewId = sessionStorage.getItem("interviewId");
  const companyId = sessionStorage.getItem("companyId");

  if (!meetingId || !interviewId || !companyId) {
    console.error("[Whiteboard] Missing session values —", {
      meetingId,
      interviewId,
      companyId,
    });
  }

  if (!questionId) {
    console.error(
      "[Whiteboard] Missing questionId — socket URL will contain " +
        '"undefined". Pass the question_id (fresh open) or ' +
        "whiteboard_question_id (reconnect) through to getWhiteboardSocket().",
    );
  }

  return `api/ws/whiteboard/${meetingId}/${boardId}/${interviewId}/${questionId}/${companyId}`;
}

// Wraps a raw WebSocket with a socket.io-style .on/.off/.emit surface,
// so consumer code doesn't need a full rewrite. Wire format is a plain
// JSON envelope: { event: "whiteboard-update", data: {...} }.
// IMPORTANT: this only works if the whiteboard backend actually sends/
// expects this envelope shape — confirm with backend before relying on it.
function attachEventShim(ws) {
  ws._listeners = new Map();

  // Messages emitted while the handshake is still in flight (readyState
  // CONNECTING) get queued here instead of dropped, then flushed once
  // "open" fires. Without this, anything emitted synchronously right
  // after getWhiteboardSocket() — e.g. "start-recording" / "join-board"
  // on mount — is silently lost, since a fresh WebSocket is never OPEN
  // on the same tick it's constructed.
  ws._sendQueue = [];

  const flushQueue = () => {
    while (ws._sendQueue.length) {
      ws.send(ws._sendQueue.shift());
    }
  };
  ws.addEventListener("open", flushQueue);

  ws.on = (event, cb) => {
    if (!ws._listeners.has(event)) ws._listeners.set(event, new Set());
    ws._listeners.get(event).add(cb);
  };

  ws.off = (event, cb) => {
    ws._listeners.get(event)?.delete(cb);
  };

  ws.emit = (event, data) => {
    const payload = JSON.stringify({ event, data });
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(payload);
    } else if (ws.readyState === WebSocket.CONNECTING) {
      ws._sendQueue.push(payload);
      console.warn(`[Whiteboard] Queuing "${event}" — socket still connecting`);
    } else {
      console.warn(
        `[Whiteboard] Cannot emit "${event}" — socket is ${
          ws.readyState === WebSocket.CLOSING ? "closing" : "closed"
        }`,
      );
    }
  };

  // Native WebSocket connects the instant it's constructed — there's no
  // separate connect step. Kept as a no-op so existing socket.connect()
  // call sites don't need to be hunted down individually.
  ws.connect = () => {};

  ws.onmessage = (e) => {
    let parsed;
    try {
      parsed = JSON.parse(e.data);
    } catch {
      console.warn("[Whiteboard] Non-JSON message ignored:", e.data);
      return;
    }
    const { event, data } = parsed || {};
    ws._listeners.get(event)?.forEach((cb) => cb(data));
  };
}

// Force-closes the current whiteboard socket (if any) and clears the
// singleton reference. The next call to getWhiteboardSocket() will then
// always open a brand new WebSocket handshake instead of reusing a
// still-OPEN connection.
//
// This matters for multi-question whiteboards: each question is a new
// "join-board" on the *same* transport unless we explicitly tear it
// down, so WhiteboardView calls this on unmount (i.e. every time it
// remounts for the next question) to guarantee each question really
// does get its own fresh socket connection, not just a new room on a
// recycled one.
export function resetWhiteboardSocket() {
  if (_socket) {
    try {
      // Detach the shim's message handler first so any late-arriving
      // frames from the closing socket don't get routed to listeners
      // that belong to a WhiteboardView instance which is unmounting.
      _socket.onmessage = null;
      if (
        _socket.readyState === WebSocket.OPEN ||
        _socket.readyState === WebSocket.CONNECTING
      ) {
        _socket.close(); 
      }
    } catch (err) {
      console.warn("[Whiteboard] Error closing socket during reset:", err);
    }
  }
  _socket = null;
  _socketBoardId = null;
  _socketQuestionId = null;
  _notifySubscribers();
}

// Pass the current question's sessionId as boardId so each question gets
// its own room on the wire (URL path), not just in the join/update/clear
// event payloads. questionId is the coding question's own id — question_id
// from get-whiteboard-coding-questions on a fresh open, or
// whiteboard_question_id from pre-reconnection-check when resuming. A
// change in EITHER boardId or questionId always forces a fresh connection,
// even if resetWhiteboardSocket() wasn't called for some reason — this is
// a safety net on top of WhiteboardView's unmount-triggered reset, not a
// replacement for it.
export function getWhiteboardSocket(boardId = "board-1", questionId,skipReconnect = false) {
  if (!questionId) {
    console.log(
      "[Whiteboard] Delaying socket connection — questionId is null/undefined",
    );
    return null;
  }
  const needsNewSocket =
    !_socket ||
    (!skipReconnect && _socket.readyState === WebSocket.CLOSED) ||
    _socketBoardId !== boardId ||
    _socketQuestionId !== questionId;

  if (needsNewSocket) {
    // resetWhiteboardSocket(); // no-op if there's nothing to close

    const socketUrl = `${WB_BASE}${getWhiteboardPath(boardId, questionId)}`;
    console.log("[Whiteboard] Connecting to:", socketUrl);

    _socket = new WebSocket(socketUrl);
    _socketBoardId = boardId;
    _socketQuestionId = questionId;
    attachEventShim(_socket);

    _socket.addEventListener("open", () =>
      console.log("✅ Whiteboard Connected"),
    );
    _socket.addEventListener("error", (err) =>
      console.log("❌ CONNECT ERROR", err),
    );
    _socket.addEventListener("close", (e) =>
      console.log("❌ DISCONNECTED", e.code, e.reason),
    );

    _notifySubscribers();
  }
  return _socket;
}